import { Alert, Box, Link, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { BACKEND_BASE_URL } from '../constants';
import { googleDocJsonToSanitizedHtml } from '../lib/googleDocsHtml';
import LoadingIndicator from './LoadingIndicator';

interface Props {
    docId: string;
    tabId: string | null;
    token: string | null | undefined;
    /** When false, auth has settled (avoids flashing "sign in" while token loads). */
    authReady: boolean;
    viewOnly: boolean;
    viewUrl: string | null;
}

/** Dedupe key: doc + tab only (token is read from a ref at fetch time so auth churn does not start parallel requests). */
function docTabKey(docId: string, tabId: string | null): string {
    return `${docId}\0${tabId ?? ''}`;
}

export default function WelcomeDocHtmlPanel(props: Props) {
    const { docId, tabId, token, authReady, viewOnly, viewUrl } = props;
    const [html, setHtml] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Latest auth token for the in-flight request (avoids refetching when only the token reference changes). */
    const latestTokenRef = useRef<string | undefined>(undefined);
    latestTokenRef.current = token ?? undefined;

    const loadedKeyRef = useRef<string | null>(null);
    const inFlightKeyRef = useRef<string | null>(null);
    /** Stale responses are ignored when doc/tab changes or inputs become invalid (no AbortController — we never cancel). */
    const fetchGenerationRef = useRef(0);

    // Temporary debug: log every html state transition so we can see what the DOM actually receives.
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        const hasImg = html ? /<img\b/i.test(html) : false;
        console.log('[WelcomeDocHtmlPanel] html state changed', {
            isNull: html === null,
            length: html?.length ?? 0,
            hasImgTag: hasImg,
            preview: html ? html.slice(0, 120) : null,
        });
    }, [html]);

    useEffect(() => {
        if (!docId || viewOnly || !authReady || !token) {
            fetchGenerationRef.current += 1;
            loadedKeyRef.current = null;
            inFlightKeyRef.current = null;
            setHtml(null);
            setError(null);
            setLoading(false);
            return;
        }

        const key = docTabKey(docId, tabId);
        if (loadedKeyRef.current === key) {
            return;
        }
        if (inFlightKeyRef.current === key) {
            return;
        }
        inFlightKeyRef.current = key;

        const generation = ++fetchGenerationRef.current;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const authToken = latestTokenRef.current;
                if (!authToken) {
                    return;
                }
                const params = new URLSearchParams({ documentId: docId });
                const res = await fetch(`${BACKEND_BASE_URL}/export-doc-json?${params.toString()}`, {
                    headers: { token: authToken },
                });
                const text = await res.text();
                if (generation !== fetchGenerationRef.current) return;
                if (!res.ok) {
                    let errMsg = text;
                    try {
                        const j = JSON.parse(text) as { error?: string };
                        if (j.error) errMsg = j.error;
                    } catch {
                        /* use raw text */
                    }
                    throw new Error(errMsg || 'Failed to fetch document');
                }
                const safeHtml = googleDocJsonToSanitizedHtml(text, tabId);
                setHtml(safeHtml);
                loadedKeyRef.current = key;
            } catch (e) {
                if (generation !== fetchGenerationRef.current) return;
                setHtml(null);
                loadedKeyRef.current = null;
                setError(e instanceof Error ? e.message : 'Failed to render document');
            } finally {
                if (inFlightKeyRef.current === key) {
                    inFlightKeyRef.current = null;
                }
                if (generation === fetchGenerationRef.current) {
                    setLoading(false);
                }
            }
        })();
    }, [docId, tabId, token, viewOnly, authReady]);

    if (viewOnly) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                    Info document is not shown in view-only mode.
                </Typography>
                {viewUrl && (
                    <Link href={viewUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                        Open in Google Docs
                    </Link>
                )}
            </Box>
        );
    }

    if (!authReady) {
        return <LoadingIndicator message="Loading…" />;
    }

    if (!token) {
        return (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                Sign in to view this document in the app.
            </Typography>
        );
    }

    // Only block on loading when there is nothing to show yet. Otherwise refetches set
    // loading=true and would replace the doc with a spinner, unmounting <img> and killing network requests.
    if (loading && !html) {
        return <LoadingIndicator message="Loading document…" />;
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!html) {
        return null;
    }

    return (
        <Box
            className="welcome-doc-html"
            sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                minHeight: 0,
                '& h1': { fontSize: '1.75rem', fontWeight: 600, mt: 2, mb: 1 },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1 },
                '& h3': { fontSize: '1.2rem', fontWeight: 600, mt: 1.5, mb: 0.75 },
                '& h4, & h5, & h6': { fontWeight: 600, mt: 1.5, mb: 0.5 },
                '& p': {
                    // mb: 1,
                    lineHeight: 1,
                    // Empty paragraphs from Google Docs would otherwise collapse to zero height
                    minHeight: '1em',
                },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1, textAlign: 'left' },
                '& u': { textDecoration: 'underline' },
                '& img': { maxWidth: '100%', height: 'auto' },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
